import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../utils/api";

/**
 * 합주방 (2026-10-10, 사장님 10/9 결정: 방장만 구독, 들어오는 사람은 로그인만 하면 무료).
 * 방장이 재생·멈춤·위치·속도·키를 바꾸면 서버(/ensemble/{code}/state)에 쓰고,
 * 들어온 사람은 1초마다 읽어 같은 화면으로 맞춘다. 링크: /notes/{곡}?room={code}
 */

export interface EnsembleState {
  playing: boolean;
  progress: number;
  speed: number;
  key: number;
  capo: any;
}

interface Args {
  musicId: number;
  enabled: boolean;
  local: EnsembleState;
  interval: number; // 지금 속도에서 한 박(25) 나아가는 데 걸리는 ms
  apply: (s: Partial<EnsembleState>) => void;
}

export const roomFromUrl = () => {
  try {
    const r = new URLSearchParams(window.location.search).get("room");
    return r && /^[a-z0-9]{6,12}$/i.test(r) ? r.toLowerCase() : null;
  } catch (e) {
    return null;
  }
};

export const roomLink = (musicId: number, code: string) => `${window.location.origin}/notes/${musicId}?room=${code}`;

export const useEnsemble = ({ musicId, enabled, local, interval, apply }: Args) => {
  const [code, setCode] = useState<string | null>(() => roomFromUrl());
  const [isHost, setIsHost] = useState(false);
  const [hostName, setHostName] = useState("");
  const [closed, setClosed] = useState(false);
  const ver = useRef(-1);
  const localRef = useRef(local);
  localRef.current = local;
  const intervalRef = useRef(interval);
  intervalRef.current = interval;
  const applyRef = useRef(apply);
  applyRef.current = apply;

  // 방 정보 + (들어온 사람) 1초마다 방장 상태 따라가기
  useEffect(() => {
    if (!enabled || !code || !musicId) return;
    let stop = false;
    let timer: any;
    const tick = async () => {
      try {
        const { data } = await api.get(`/ensemble/${code}`);
        if (stop) return;
        if (!data.success) {
          setClosed(true);
          return;
        }
        const d = data.data;
        setHostName(d.host || "방장");
        setIsHost(!!d.is_host);
        if (d.is_host) return; // 방장은 읽지 않고 쓰기만
        const s = d.state as (EnsembleState & { sent_ms: number }) | null;
        if (s) {
          const changed = d.ver !== ver.current;
          ver.current = d.ver;
          const cur = localRef.current;
          const patch: Partial<EnsembleState> = {};
          if (changed) {
            if (s.speed !== cur.speed) patch.speed = s.speed;
            if (s.key !== cur.key) patch.key = s.key;
            if (JSON.stringify(s.capo ?? null) !== JSON.stringify(cur.capo ?? null)) patch.capo = s.capo;
          }
          if (s.playing) {
            // 방장이 마지막으로 보낸 뒤 흐른 시간만큼 앞으로
            const beats = Math.max(0, (d.server_ms - s.sent_ms) / Math.max(50, intervalRef.current));
            const expected = Math.round((s.progress + beats * 25) / 25) * 25;
            if (Math.abs(cur.progress - expected) > 50) patch.progress = expected;
            if (!cur.playing) patch.playing = true;
          } else {
            if (cur.playing) patch.playing = false;
            if (changed && cur.progress !== s.progress) patch.progress = s.progress;
          }
          if (Object.keys(patch).length) applyRef.current(patch);
        }
      } catch (e) {
        // 잠깐 끊겨도 다음 번에 다시
      }
      if (!stop) timer = setTimeout(tick, 1000);
    };
    tick();
    return () => {
      stop = true;
      clearTimeout(timer);
    };
  }, [enabled, code, musicId]);

  // 방장: 바뀔 때마다, 재생 중에는 2초마다 서버에 쓰기
  const lastSent = useRef("");
  const send = useCallback(
    (force = false) => {
      if (!code || !isHost) return;
      const s = localRef.current;
      const key = JSON.stringify([s.playing, s.speed, s.key, s.capo]);
      if (!force && key === lastSent.current) return;
      lastSent.current = key;
      api.post(`/ensemble/${code}/state`, { state: s }).catch(() => null);
    },
    [code, isHost]
  );
  useEffect(() => {
    send();
  }, [send, local.playing, local.speed, local.key, local.capo]);
  // 재생 안 할 때 위치를 옮기면(마디 누르기·처음으로) 바로 알림
  const lastProgress = useRef(local.progress);
  useEffect(() => {
    const jumped = Math.abs(local.progress - lastProgress.current) > 25;
    lastProgress.current = local.progress;
    if (jumped || !local.playing) send(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local.progress]);
  useEffect(() => {
    if (!isHost || !local.playing) return;
    const t = setInterval(() => send(true), 2000);
    return () => clearInterval(t);
  }, [isHost, local.playing, send]);

  const open = useCallback(async () => {
    const { data } = await api.post("/ensemble", { music_id: musicId });
    if (!data.success) throw new Error(data.alert || "합주방을 열지 못했어요.");
    const c = data.data.code as string;
    setCode(c);
    setIsHost(true);
    setClosed(false);
    try {
      window.history.replaceState(null, "", `/notes/${musicId}?room=${c}`);
    } catch (e) {}
    lastSent.current = "";
    return c;
  }, [musicId]);

  const close = useCallback(async () => {
    if (code) await api.post(`/ensemble/${code}/close`).catch(() => null);
    setCode(null);
    setIsHost(false);
    try {
      window.history.replaceState(null, "", `/notes/${musicId}`);
    } catch (e) {}
  }, [code, musicId]);

  const leave = useCallback(() => {
    setCode(null);
    try {
      window.history.replaceState(null, "", `/notes/${musicId}`);
    } catch (e) {}
  }, [musicId]);

  return { code, isHost, hostName, closed, open, close, leave };
};
